/* H23: count-up and bar-fill animation for the survey results.
 *
 * Progressive enhancement, deliberately. The markup already contains the final
 * numbers and bar widths, so with JS off, or if this file fails to load, the
 * section reads exactly as it does today. Nothing is zeroed out until we know
 * we can animate it back.
 *
 * Anyone who has asked their OS for reduced motion gets the final values
 * immediately. Counting digits and sliding bars are precisely the kind of
 * motion that setting exists to suppress.
 */
(function () {
  'use strict';

  var DURATION = 1100;
  var STAGGER = 70;
  var MAX_STAGGER = 420;

  function prefersNoMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /* easeOutCubic: quick off the mark, settles gently on the number */
  function ease(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  /* "86%" -> { value: 86, prefix: "", suffix: "%" } */
  function parseValue(text) {
    var match = String(text).match(/^(\D*?)(\d+(?:\.\d+)?)(\D*)$/);
    if (!match) return null;
    return {
      value: parseFloat(match[2]),
      decimals: (match[2].split('.')[1] || '').length,
      prefix: match[1] || '',
      suffix: match[3] || ''
    };
  }

  function countUp(el, parsed) {
    var start = null;

    function frame(now) {
      if (start === null) start = now;
      var t = Math.min((now - start) / DURATION, 1);
      var current = parsed.value * ease(t);
      el.textContent =
        parsed.prefix + current.toFixed(parsed.decimals) + parsed.suffix;
      if (t < 1) {
        requestAnimationFrame(frame);
      } else {
        // land exactly on the authored string, never a rounding artefact
        el.textContent = parsed.prefix + parsed.value.toFixed(parsed.decimals) + parsed.suffix;
      }
    }

    requestAnimationFrame(frame);
  }

  function collect() {
    var groups = [];

    var surveyCards = document.querySelectorAll('.h23-survey__card');
    for (var i = 0; i < surveyCards.length; i++) {
      var card = surveyCards[i];
      groups.push({
        root: card,
        number: card.querySelector('.h23-survey__pct'),
        fill: card.querySelector('.h23-survey__bar > *')
      });
    }

    // the product page proof band carries the same figures, without bars
    var proofStats = document.querySelectorAll('.h23pr__stat');
    for (var j = 0; j < proofStats.length; j++) {
      groups.push({
        root: proofStats[j],
        number: proofStats[j].querySelector('.h23pr__value'),
        fill: null
      });
    }

    return groups.filter(function (g) {
      return g.number && !g.root.hasAttribute('data-h23-stat-ready');
    });
  }

  function prime(group) {
    group.root.setAttribute('data-h23-stat-ready', '');

    var parsed = parseValue(group.number.textContent.trim());
    if (!parsed) return null;
    group.parsed = parsed;

    // hold the rendered width so the card cannot reflow as digits change
    var box = group.number.getBoundingClientRect();
    if (box.width) group.number.style.minWidth = Math.ceil(box.width) + 'px';

    group.number.textContent = parsed.prefix + (0).toFixed(parsed.decimals) + parsed.suffix;

    if (group.fill) {
      group.targetWidth = group.fill.style.width || getComputedStyle(group.fill).width;
      group.fill.style.width = '0%';
    }

    return group;
  }

  function finalText(parsed) {
    return parsed.prefix + parsed.value.toFixed(parsed.decimals) + parsed.suffix;
  }

  /* Snap straight to the authored values, no animation. */
  function complete(group) {
    if (group.done) return;
    group.done = true;
    group.number.textContent = finalText(group.parsed);
    if (group.fill && group.targetWidth) group.fill.style.width = group.targetWidth;
  }

  function run(group, index) {
    if (group.done) return;
    group.done = true;

    var delay = Math.min(index * STAGGER, MAX_STAGGER);

    setTimeout(function () {
      countUp(group.number, group.parsed);
      if (group.fill && group.targetWidth) {
        group.fill.style.width = group.targetWidth;
      }

      /* Watchdog. requestAnimationFrame is suspended in background tabs, so
       * countUp can start and never advance, leaving "0%" where the proof
       * should be. Timers still fire (throttled), so guarantee the landing. */
      setTimeout(function () {
        var want = finalText(group.parsed);
        if (group.number.textContent.trim() !== want) group.number.textContent = want;
      }, DURATION + 800);
    }, delay);
  }

  function init() {
    var groups = collect();
    if (!groups.length) return;

    if (prefersNoMotion() || typeof IntersectionObserver !== 'function') {
      // leave the authored values exactly as they are
      for (var i = 0; i < groups.length; i++) {
        groups[i].root.setAttribute('data-h23-stat-ready', '');
      }
      return;
    }

    var primed = [];
    for (var j = 0; j < groups.length; j++) {
      var p = prime(groups[j]);
      if (p) primed.push(p);
    }

    var seen = 0;
    var observer = new IntersectionObserver(
      function (entries) {
        for (var k = 0; k < entries.length; k++) {
          var entry = entries[k];
          if (!entry.isIntersecting) continue;
          var group = entry.target.__h23Stat;
          observer.unobserve(entry.target);
          if (group) run(group, seen++);
        }
      },
      { threshold: 0.35, rootMargin: '0px 0px -8% 0px' }
    );

    for (var m = 0; m < primed.length; m++) {
      primed[m].root.__h23Stat = primed[m];
      observer.observe(primed[m].root);
    }

    /* Safety net.
     *
     * Priming sets every figure to zero. If the observer never delivers, a
     * visitor would be left staring at "0%" where the proof should be, which
     * is far worse than losing an animation. Anything still waiting after a
     * few seconds gets snapped to its real value.
     *
     * Timers are throttled in background tabs, so re-arm a short sweep
     * whenever the page becomes visible again.
     */
    function sweep() {
      for (var n = 0; n < primed.length; n++) {
        var g = primed[n];
        if (g.done) continue;
        var box = g.root.getBoundingClientRect();
        var onScreen = box.top < innerHeight && box.bottom > 0;
        if (onScreen) {
          observer.unobserve(g.root);
          run(g, seen++);
        } else {
          complete(g);
        }
      }
    }

    setTimeout(sweep, 5000);

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) setTimeout(sweep, 1200);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // sections can be re-rendered by the theme editor
  document.addEventListener('shopify:section:load', init);
})();
